import { first } from "es-toolkit/compat";
import { atom } from "jotai";
import { atomWithCache } from "jotai-cache";
import { atomFamily } from "jotai/utils";
import { EditorState } from "prosemirror-state";

import { parsePage } from "@moment/api-collab/markdown";
import { Logger } from "@moment/logging";
import { schema } from "@moment/prosemirror-schema";

import { $accessTokenFunc } from "../authenication";
import { $document } from "../document-list";
import { $documentMetadata } from "../document-metadata/metadata-state.atom";
import { env } from "../env";
import { fs } from "../filesystem";
import { paths } from "../paths";
import { type PageContentResult, type PageMetadata, type StoredPage } from "./page.types";

const logger = new Logger("page");

const createEmptyEditorState = () => {
	const doc = schema.topNodeType.createAndFill();
	if (!doc) {
		throw new Error("Failed to create empty ProseMirror doc");
	}

	return EditorState.create({ doc, schema });
};

const createEmptyStoredPage = (pageId: string): StoredPage => {
	const editorState = createEmptyEditorState();

	return {
		doc: editorState.doc.toJSON(),
		metadata: {
			id: pageId,
			slug: pageId,
			title: "",
			archived: false,
			parentPageID: null,
			type: "markdown",
		},
	};
};

type ParsedMarkdownPage = ReturnType<typeof parsePage>;

const isPageType = (value: unknown): value is NonNullable<PageMetadata["type"]> => {
	return value === "markdown" || value === "canvas" || value === "request";
};

const parsePublishedOn = (value: unknown): Date | undefined => {
	if (value instanceof Date) {
		return value;
	}

	if (typeof value === "string" && value.trim().length > 0) {
		const timestamp = Date.parse(value);
		if (!Number.isNaN(timestamp)) {
			return new Date(timestamp);
		}
	}

	return undefined;
};

const normalizePageMetadata = (
	pageId: string,
	metadata?: Record<string, unknown>
): PageMetadata => {
	const candidate = metadata ?? {};
	const archivedValue = candidate["archived"];

	const archived =
		typeof archivedValue === "boolean"
			? archivedValue
			: typeof archivedValue === "string"
				? archivedValue.toLowerCase() === "true"
				: false;

	const parentPageID =
		typeof candidate["parentPageID"] === "string"
			? (candidate["parentPageID"] as string)
			: candidate["parentPageID"] === null
				? null
				: null;

	const typeValue = candidate["type"];
	const pageType = isPageType(typeValue) ? typeValue : "markdown";

	return {
		id: typeof candidate["id"] === "string" ? (candidate["id"] as string) : pageId,
		slug: typeof candidate["slug"] === "string" ? (candidate["slug"] as string) : pageId,
		title: typeof candidate["title"] === "string" ? (candidate["title"] as string) : "",
		archived,
		parentPageID,
		type: pageType,
		authorID:
			typeof candidate["authorID"] === "string"
				? (candidate["authorID"] as string)
				: undefined,
		publishedOn: parsePublishedOn(candidate["publishedOn"]),
	};
};

const toStoredPage = (pageId: string, parsed: ParsedMarkdownPage): StoredPage => ({
	doc: parsed.doc,
	metadata: normalizePageMetadata(pageId, parsed.metadata),
});

// ============================================================================
// Page Metadata Atoms
// ============================================================================

/**
 * Get page metadata by documentId and pageId
 */
export const $pageMetadata = atomFamily(
	({ documentId, pageId }: { documentId: string; pageId: string }) =>
		atom(async (get) => {
			const metadata = await get($documentMetadata(documentId));
			if (!metadata) {
				return undefined;
			}

			return metadata.pagesMetadata.find((p) => p.id === pageId);
		})
);

/**
 * Find page by slug with fallback logic:
 * 1. Match by slug
 * 2. Fallback to "index" page
 * 3. Fallback to match by id
 * 4. Fallback to first page
 */
export const $pageBySlug = atomFamily(
	({ documentId, slug }: { documentId: string; slug: string | undefined }) =>
		atom(async (get) => {
			const metadata = await get($documentMetadata(documentId));
			if (!metadata) {
				return undefined;
			}

			const pages = metadata.pagesMetadata;

			// Try to find by slug
			if (slug) {
				const bySlug = pages.find((page) => page.slug === slug);
				if (bySlug) {
					return bySlug;
				}
			}

			// Fallback to "index" page
			const indexPage = pages.find((page) => page.slug === "index");
			if (indexPage) {
				return indexPage;
			}

			// Fallback to match by id
			if (slug) {
				const byId = pages.find((page) => page.id === slug);
				if (byId) {
					return byId;
				}
			}

			// Fallback to first page
			return pages[0];
		})
);

// ============================================================================
// Page Content Loading Atoms
// ============================================================================

/**
 * Load page content from disk
 */
const $pageContentFromDisk = atomFamily(
	({ documentId, pageId }: { documentId: string; pageId: string }) => {
		const contentAtom = atom(async (get) => {
			const enabled = !env.isSsr && env.isDesktopApp;

			if (!enabled) {
				return undefined;
			}

			const document = await get($document(documentId));
			if (!document?.local) {
				return undefined;
			}

			const documentPath = Object.values(document.local)[0]?.path;
			if (!documentPath) {
				return undefined;
			}

			const fsApi = fs();
			const pathsApi = paths();

			// Get page metadata to check for explicit path
			const pageMetadata = await get($pageMetadata({ documentId, pageId }));
			const pagePath = pageMetadata?.path;

			const pageFilePath = await get(
				pathsApi.getPageFilePath({ documentId, pageId, pagePath })
			);
			const exists = await get(fsApi.exists({ filePath: pageFilePath }));

			if (!exists) {
				logger.warn("Page file does not exist on disk", {
					documentId,
					pageId,
					pageFilePath,
				});
				return undefined;
			}

			const raw = await get(fsApi.readTextFile({ filePath: pageFilePath }));

			// Handle empty files
			if (raw.trim() === "") {
				return { parsed: createEmptyStoredPage(pageId), raw };
			}

			const parsed = parsePage(raw);

			return { parsed: toStoredPage(pageId, parsed), raw };
		});
		contentAtom.debugLabel = `pageContent.disk/${documentId}/${pageId}`;

		return contentAtom;
	}
);

/**
 * Load page content from remote
 */
const $pageContentFromRemote = atomFamily(
	({ documentId, pageId }: { documentId: string; pageId: string }) => {
		const contentAtom = atom(async (get) => {
			if (env.isSsr) {
				return undefined;
			}

			const document = await get($document(documentId));
			if (!document?.remote) {
				return undefined;
			}

			const workspace = first(Object.values(document.remote))?.workspace;
			if (!workspace) {
				return undefined;
			}

			const { getAccessTokenSilently } = get($accessTokenFunc);
			if (!getAccessTokenSilently) {
				logger.warn("No auth token available for loading page from remote");
				return undefined;
			}

			const token = await getAccessTokenSilently();

			try {
				// Build the URL for fetching page content
				const url = `${env.docsApiUrl}/api/v2/${workspace.name}/${documentId}/raw/pages/${pageId}/index.md?ref=main`;

				const response = await fetch(url, {
					headers: {
						Accept: "application/json",
						Authorization: `Bearer ${token}`,
					},
				});

				if (!response.ok) {
					logger.error("Failed to fetch page contents from remote", {
						url,
						status: response.status,
					});
					return undefined;
				}

				const _text = await response.text();
				const text = _text.trim().length === 0 ? "<br />" : _text;

				const { doc: jsonDoc, metadata } = parsePage(text);

				const parsed: StoredPage = {
					doc: jsonDoc,
					metadata: {
						id: pageId,
						slug: pageId,
						title: "",
						archived: false,
						parentPageID: null,
						...metadata,
					},
				};

				return { parsed, raw: text };
			} catch (error) {
				logger.error("Error fetching page content from remote", {
					error,
					documentId,
					pageId,
				});
				return undefined;
			}
		});
		contentAtom.debugLabel = `pageContent.remote/${documentId}/${pageId}`;

		return contentAtom;
	}
);

/**
 * Main page content atom that combines disk and remote sources
 */
export const $pageContent = atomFamily(
	({ documentId, pageId }: { documentId: string; pageId: string }) =>
		atomWithCache(async (get): Promise<PageContentResult | undefined> => {
			const document = await get($document(documentId));

			if (!document) {
				// Document is still loading or doesn't exist, no need to log
				return undefined;
			}

			// Try to load from disk first
			if (document.local) {
				const diskContent = await get($pageContentFromDisk({ documentId, pageId }));

				if (diskContent) {
					return {
						source: "disk",
						content: diskContent.parsed,
						raw: diskContent.raw,
					};
				}
			}

			// Fallback to remote
			if (document.remote) {
				const remoteContent = await get($pageContentFromRemote({ documentId, pageId }));

				if (remoteContent) {
					return {
						source: "remote",
						content: remoteContent.parsed,
						raw: remoteContent.raw,
					};
				}
			}

			return undefined;
		})
);
